import { useEffect, useState } from "react";
import { repo } from "./ch/planrepo";
import { endOfWeek, addWeeks, isAfter, format, isToday } from "date-fns";
import { RacePlan } from "./ch/dategrid";
import { build, swap } from "./ch/planbuilder";
import { toIcal } from "./ch/icalservice";
import { toCsv } from "./ch/csvService";
import { toNotion } from "./ch/notionService";
import { toGarmin } from "./ch/garminService";
import { toStrava } from "./ch/stravaService";
import { download } from "./ch/downloadservice";
import { renderDist, renderStr, getWeekDistance } from "./ch/rendering";
import {
  useQueryParams,
  StringParam,
  DateParam,
  NumberParam,
} from "use-query-params";
import { WeekStartsOn, WeekStartsOnValues } from "./ch/datecalc";
import { useMountEffect } from "./ch/hooks";
import { Units, PlanSummary, Week, DayDetails, Sport, RaceType } from "types/app";
import { getLocaleUnits } from "./ch/localize";
import { useDrag, useDrop } from "react-dnd";
import { ItemTypes } from "./ch/ItemTypes";
import { applyOnboardingConfig } from "./ch/onboardingBuilder";
import {
  decodeLongRunDay,
  decodeTrainingDays,
  encodeLongRunDay,
  encodeTrainingDays,
} from "./ch/shareableConfig";
import { TopNav } from "./components/TopNav";
import logoImage from "./assets/progress-plans-logo.png";
import "./dashboard.css";

const DAYS_OF_WEEK: { full: string; abbr: string }[] = [
  { full: "Monday", abbr: "Mon" },
  { full: "Tuesday", abbr: "Tue" },
  { full: "Wednesday", abbr: "Wed" },
  { full: "Thursday", abbr: "Thu" },
  { full: "Friday", abbr: "Fri" },
  { full: "Saturday", abbr: "Sat" },
  { full: "Sunday", abbr: "Sun" },
];

function sportFromRaceType(raceType: RaceType): Sport {
  if (
    raceType === "50m" ||
    raceType === "100m" ||
    raceType === "400m" ||
    raceType === "1500m" ||
    raceType === "5km Swim" ||
    raceType === "10km Swim"
  ) {
    return "swimming";
  }
  if (
    raceType === "20km Ride" ||
    raceType === "40km Ride" ||
    raceType === "80km Ride" ||
    raceType === "100km Ride" ||
    raceType === "160km Ride"
  ) {
    return "cycling";
  }
  if (
    raceType === "Hyrox Women Open" ||
    raceType === "Hyrox Women Pro" ||
    raceType === "Hyrox Men Open" ||
    raceType === "Hyrox Men Pro"
  ) {
    return "hyrox";
  }
  if (
    raceType === "Sprint Triathlon" ||
    raceType === "Standard Triathlon" ||
    raceType === "Half Ironman" ||
    raceType === "Ironman"
  ) {
    return "triathlon";
  }
  return "running";
}

function filterPlansBySport(plans: PlanSummary[], sport: Sport): PlanSummary[] {
  return plans.filter((p) => sportFromRaceType(p[2]) === sport);
}

function pickPlanForSport(
  plans: PlanSummary[],
  planId: string | undefined,
  sport: Sport,
): PlanSummary {
  const availableForSport = filterPlansBySport(plans, sport);
  const fallback = availableForSport[0] ?? plans[0];

  if (!planId) return fallback;

  const found = repo.find(planId);
  if (found && sportFromRaceType(found[2]) === sport) return found;

  return fallback;
}

function computeRequiredDays(trainingPlan: any): number {
  const weeks: any[] | undefined = trainingPlan?.schedule;
  if (!Array.isArray(weeks) || weeks.length === 0) return 0;

  const countWeek = (week: any): number => {
    const workouts: any[] | undefined = week?.workouts;
    if (!Array.isArray(workouts)) return 0;
    return workouts.filter((w) => {
      const title = String(w?.title ?? "").toLowerCase();
      return title && title !== "rest" && !title.includes("rest");
    }).length;
  };

  return weeks.reduce((max, week) => Math.max(max, countWeek(week)), 0);
}

// Week selector with gradient bar chart
const WeekSelector = ({
  weeks,
  selectedWeek,
  onSelectWeek,
  units,
  currentWeekIndex,
}: {
  weeks: Week<DayDetails>[];
  selectedWeek: number;
  onSelectWeek: (week: number) => void;
  units: Units;
  currentWeekIndex: number;
}) => {
  const weekDistances = weeks.map((w) => {
    const dist = getWeekDistance(w, units);
    return dist.length === 2 ? dist[1] : dist[0];
  });
  const maxDistance = Math.max(...weekDistances);

  return (
    <div className="card week-selector">
      <div className="card-header">
        <h3>Training Overview</h3>
        <span className="card-subtitle">Weekly mileage progression</span>
      </div>
      <div className="chart-container">
        <div className="bar-chart">
          {weeks.map((_, idx) => {
            const height = (weekDistances[idx] / maxDistance) * 100;
            const isSelected = idx === selectedWeek;
            const isCurrent = idx === currentWeekIndex;
            return (
              <div
                key={idx}
                className={`bar-wrapper ${isSelected ? 'selected' : ''} ${isCurrent ? 'current' : ''}`}
                onClick={() => onSelectWeek(idx)}
              >
                <div 
                  className="bar"
                  style={{ height: `${height}%` }}
                />
                <span className="bar-label">W{idx + 1}</span>
                {isSelected && (
                  <span className="bar-value">{Math.round(weekDistances[idx])} {units}</span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Single day workout card
const DayCard = ({
  day,
  units,
  sourceUnits,
  onSwap,
  dayIndex,
}: {
  day: { date: Date; event: DayDetails | undefined };
  units: Units;
  sourceUnits: Units;
  onSwap: (d1: Date, d2: Date) => void;
  dayIndex: number;
}) => {
  const [{ isDragging }, drag, preview] = useDrag({
    type: ItemTypes.DAY,
    item: { date: day.date, dayDetails: day.event, units: units },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [{ isOver, canDrop }, drop] = useDrop({
    accept: ItemTypes.DAY,
    canDrop: (item: { date: Date }) => day.event !== undefined && day.date !== item.date,
    drop: (item: { date: Date }) => {
      onSwap(day.date, item.date);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  const dayName = format(day.date, "EEE");
  const dayNum = format(day.date, "d");
  const month = format(day.date, "MMM");
  const isTodayDate = isToday(day.date);

  let distanceStr = "";
  let title = "";
  let desc = "";
  
  if (day.event) {
    const dist = day.event.dist;
    if (dist !== undefined) {
      if (typeof dist === "number") {
        distanceStr = renderDist([dist], sourceUnits, units);
      } else if (Array.isArray(dist) && dist.length > 0) {
        distanceStr = renderDist(dist, sourceUnits, units);
      }
    }
    title = day.event.title ? renderStr(day.event.title, sourceUnits, units) : "";
    desc = day.event.desc ? renderStr(day.event.desc, sourceUnits, units) : "";
  }

  const workoutType = day.event?.type ?? null;
  const isRest = !day.event;
  const isRace = workoutType === "Race";
  const isLongRun = workoutType === "Long";
  const isSpeed = workoutType === "Intervals";
  const isSteady = workoutType === "Tempo";
  const isEasy = workoutType === "Easy" || workoutType === "Recovery";

  return (
    <div
      ref={(node) => {
        preview(node);
        drag(node);
        drop(node);
      }}
      className={`day-card ${isDragging ? "dragging" : ""} ${isOver && canDrop ? "drop-target" : ""} ${isRest ? "rest-day" : ""} ${isRace ? "race-day" : ""} ${isLongRun ? "long-run" : ""} ${isSpeed ? "speed-day" : ""} ${isSteady ? "steady-day" : ""} ${isEasy ? "easy-day" : ""} ${isTodayDate ? "today" : ""}`}
    >
      <div className="day-card-header">
        <div className="day-date">
          <span className="day-name">{dayName}</span>
          <span className="day-num">{dayNum}</span>
          {dayIndex === 0 && <span className="day-month">{month}</span>}
        </div>
      </div>
      
      <div className="day-card-content">
        {day.event ? (
          <>
            <div className="distance-badge">
              <span className="distance-value">{distanceStr || "—"}</span>
            </div>
            {workoutType && (
              <span className={`workout-type-badge ${workoutType.toLowerCase()}`}>
                {workoutType.toUpperCase()}
              </span>
            )}
            <div className="workout-text">
              <p className="workout-title">{title}</p>
              {desc ? <p className="workout-desc">{desc}</p> : null}
            </div>
          </>
        ) : (
          <>
            <div className="distance-badge">
              <span className="distance-value">—</span>
            </div>
            <span className="workout-type-badge rest">REST</span>
            <div className="workout-text">
              <p className="workout-title">Rest Day</p>
            </div>
          </>
        )}
      </div>
      
      {isOver && canDrop && <div className="drop-overlay" />}
    </div>
  );
};

// Stats card component
const StatCard = ({
  label,
  value,
  sublabel,
  icon,
}: {
  label: string;
  value: string;
  sublabel?: string;
  icon: string;
}) => (
  <div className="stat-card">
    <div className="stat-icon">{icon}</div>
    <div className="stat-content">
      <span className="stat-label">{label}</span>
      <span className="stat-value">{value}</span>
      {sublabel && <span className="stat-sublabel">{sublabel}</span>}
    </div>
  </div>
);

const RunDashboard = () => {
  const [{ u, p, d, s, td, lr, sport }, setq] = useQueryParams({
    u: StringParam,
    p: StringParam,
    d: DateParam,
    s: NumberParam,
    // shareable schedule prefs (optional)
    td: StringParam,
    lr: StringParam,
    // sport type (optional, defaults to running)
    sport: StringParam,
  });

  // Determine which sport we're working with
  const activeSport: Sport = (sport as Sport) || "running";

  const availablePlansForSport = filterPlansBySport(repo.available, activeSport);

  const [selectedUnits, setSelectedUnits] = useState<Units>(
    u === "mi" || u === "km" ? u : getLocaleUnits()
  );
  const [selectedPlan, setSelectedPlan] = useState<PlanSummary>(() =>
    pickPlanForSport(repo.available, p || undefined, activeSport)
  );
  const [racePlan, setRacePlan] = useState<RacePlan | undefined>(undefined);
  const [showExportModal, setShowExportModal] = useState(false);
  const [weekStartsOn] = useState<WeekStartsOn>(
    s === 0 || s === 1 || s === 6 ? s : WeekStartsOnValues.Monday
  );
  const [planEndDate, setPlanEndDate] = useState(
    d && isAfter(d, new Date())
      ? d
      : addWeeks(endOfWeek(new Date(), { weekStartsOn: weekStartsOn }), 20)
  );

  // Schedule preferences (shareable via URL)
  const [trainingDays, setTrainingDays] = useState<string[]>(
    decodeTrainingDays(td) as unknown as string[],
  );
  const [longRunDay, setLongRunDay] = useState<string>(
    decodeLongRunDay(lr) as unknown as string,
  );
  const [requiredDays, setRequiredDays] = useState<number>(0);
  const [scheduleHint, setScheduleHint] = useState<string>("");
  const [selectedWeekIndex, setSelectedWeekIndex] = useState(0);

  const ensureMinTrainingDays = (
    days: string[],
    min: number,
    preferredDay?: string,
  ): string[] => {
    const uniq = Array.from(new Set(days)).filter(Boolean);
    if (min <= 0 || uniq.length >= min) return uniq;

    const next = uniq.slice();
    if (preferredDay && !next.includes(preferredDay)) {
      next.push(preferredDay);
    }

    for (const d of DAYS_OF_WEEK.map((x) => x.full)) {
      if (next.length >= min) break;
      if (!next.includes(d)) next.push(d);
    }

    return next;
  };

  // Keep sidebar selections in sync when someone opens a bookmarked/shared URL.
  useEffect(() => {
    if (u === "mi" || u === "km") {
      setSelectedUnits(u);
    }
    setSelectedPlan(pickPlanForSport(repo.available, p || undefined, activeSport));
    if (d && isAfter(d, new Date())) {
      setPlanEndDate(d);
    }
    const tdDecoded = decodeTrainingDays(td) as unknown as string[];
    const lrDecoded = decodeLongRunDay(lr) as unknown as string;
    if (tdDecoded.length || lrDecoded) {
      setTrainingDays(tdDecoded);
      setLongRunDay(lrDecoded);
    }
    // Intentionally not rebuilding the plan here; we do that consistently on mount and on edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [u, p, d, td, lr, activeSport]);

  useMountEffect(() => {
    // Check for onboarding configuration first
    const onboardingConfig = localStorage.getItem("onboardingConfig");
    if (onboardingConfig) {
      try {
        const config = JSON.parse(onboardingConfig);
        const plan = repo.find(config.planId);
        const raceDate = new Date(config.raceDate);

        // Prepopulate schedule state so the sidebar shows the onboarding choices
        // and the URL becomes shareable immediately.
        if (Array.isArray(config.trainingDays)) {
          setTrainingDays(config.trainingDays);
        }
        if (typeof config.longRunDay === "string") {
          setLongRunDay(config.longRunDay);
        }

        // Load the plan with the onboarding configuration
        loadPlanWithConfig(plan, raceDate, selectedUnits, weekStartsOn, config);
      } catch (error) {
        console.error("Error applying onboarding config:", error);
        initialLoad(selectedPlan, planEndDate, selectedUnits, weekStartsOn);
      }
    } else {
      // If schedule prefs are present in the URL, apply them.
      const trainingDaysFromUrl = decodeTrainingDays(td);
      const longRunDayFromUrl = decodeLongRunDay(lr);
      const hasSchedulePrefs =
        trainingDaysFromUrl.length > 0 || Boolean(longRunDayFromUrl);

      if (hasSchedulePrefs) {
        loadPlanWithConfig(selectedPlan, planEndDate, selectedUnits, weekStartsOn, {
          trainingDays: trainingDaysFromUrl,
          longRunDay: longRunDayFromUrl,
        });
      } else {
        initialLoad(selectedPlan, planEndDate, selectedUnits, weekStartsOn);
      }
    }
  });

  const getParams = (
    units: Units,
    plan: PlanSummary,
    date: Date,
    weekStartsOn: WeekStartsOn
  ) => ({
    u: units,
    p: plan[0],
    d: date,
    s: weekStartsOn,
    sport: activeSport,
  });

  const getParamsWithSchedule = (
    units: Units,
    plan: PlanSummary,
    date: Date,
    weekStartsOn: WeekStartsOn,
    schedule: { trainingDays: string[]; longRunDay: string }
  ) => {
    const base = getParams(units, plan, date, weekStartsOn) as any;
    const tdEnc = encodeTrainingDays(schedule.trainingDays as any);
    const lrEnc = encodeLongRunDay(schedule.longRunDay as any);
    return {
      ...base,
      td: tdEnc ?? undefined,
      lr: lrEnc ?? undefined,
    };
  };

  const initialLoad = async (
    plan: PlanSummary,
    endDate: Date,
    units: Units,
    weekStartsOn: WeekStartsOn
  ) => {
    const trainingPlan = await repo.fetch(plan, activeSport);
    setRequiredDays(computeRequiredDays(trainingPlan));
    const racePlan = build(trainingPlan, endDate, weekStartsOn);
    setRacePlan(racePlan);
    setq(getParamsWithSchedule(units, plan, endDate, weekStartsOn, { trainingDays, longRunDay }));
  };

  /**
   * Loads plan with onboarding configuration (training days and long run day)
   */
  const loadPlanWithConfig = async (
    plan: PlanSummary,
    endDate: Date,
    units: Units,
    weekStartsOn: WeekStartsOn,
    config: { trainingDays: string[], longRunDay: string }
  ) => {
    const trainingPlan = await repo.fetch(plan, activeSport);
    const required = computeRequiredDays(trainingPlan);
    setRequiredDays(required);
    const racePlan = build(trainingPlan, endDate, weekStartsOn);

    const hasScheduleOverride =
      (Array.isArray(config.trainingDays) && config.trainingDays.length > 0) ||
      Boolean(config.longRunDay);

    const normalizedTrainingDays = hasScheduleOverride
      ? ensureMinTrainingDays(config.trainingDays ?? [], required, config.longRunDay)
      : (config.trainingDays ?? []);
    const normalizedLongRunDay =
      normalizedTrainingDays.includes(config.longRunDay) ? config.longRunDay : "";

    // Keep sidebar state consistent with what we apply.
    if (hasScheduleOverride) {
      setTrainingDays(normalizedTrainingDays);
      setLongRunDay(normalizedLongRunDay);
    }
    
    // Apply training days and long run day configuration
    const customizedPlan = applyOnboardingConfig(
      racePlan,
      normalizedTrainingDays,
      normalizedLongRunDay
    );

    setRacePlan(customizedPlan);
    setq(getParamsWithSchedule(units, plan, endDate, weekStartsOn, {
      trainingDays: normalizedTrainingDays,
      longRunDay: normalizedLongRunDay,
    }));
  };

  const onSelectedPlanChange = async (planId: string) => {
    const plan = repo.find(planId);
    const trainingPlan = await repo.fetch(plan, activeSport);
    const required = computeRequiredDays(trainingPlan);
    setRequiredDays(required);
    const racePlanBase = build(trainingPlan, planEndDate, weekStartsOn);
    const hasScheduleOverride = trainingDays.length > 0 || Boolean(longRunDay);
    const normalizedTrainingDays = hasScheduleOverride
      ? ensureMinTrainingDays(trainingDays, required, longRunDay)
      : trainingDays;
    const normalizedLongRunDay = normalizedTrainingDays.includes(longRunDay)
      ? longRunDay
      : "";
    if (hasScheduleOverride) {
      if (normalizedTrainingDays.join(",") !== trainingDays.join(",")) {
        setTrainingDays(normalizedTrainingDays);
      }
      if (normalizedLongRunDay !== longRunDay) {
        setLongRunDay(normalizedLongRunDay);
      }
    }

    const schedule = { trainingDays: normalizedTrainingDays, longRunDay: normalizedLongRunDay };
    const racePlan = schedule.trainingDays.length > 0 || schedule.longRunDay
      ? applyOnboardingConfig(racePlanBase, schedule.trainingDays, schedule.longRunDay)
      : racePlanBase;

    setSelectedPlan(plan);
    setRacePlan(racePlan);
    setSelectedWeekIndex(0);
    setq(getParamsWithSchedule(selectedUnits, plan, planEndDate, weekStartsOn, schedule));
  };

  const onSelectedEndDateChange = async (dateStr: string) => {
    const date = new Date(dateStr);
    const trainingPlan = await repo.fetch(selectedPlan, activeSport);
    const required = computeRequiredDays(trainingPlan);
    setRequiredDays(required);
    const base = build(trainingPlan, date, weekStartsOn);
    const hasScheduleOverride = trainingDays.length > 0 || Boolean(longRunDay);
    const normalizedTrainingDays = hasScheduleOverride
      ? ensureMinTrainingDays(trainingDays, required, longRunDay)
      : trainingDays;
    const normalizedLongRunDay = normalizedTrainingDays.includes(longRunDay)
      ? longRunDay
      : "";
    if (hasScheduleOverride) {
      if (normalizedTrainingDays.join(",") !== trainingDays.join(",")) {
        setTrainingDays(normalizedTrainingDays);
      }
      if (normalizedLongRunDay !== longRunDay) {
        setLongRunDay(normalizedLongRunDay);
      }
    }
    const schedule = { trainingDays: normalizedTrainingDays, longRunDay: normalizedLongRunDay };
    const racePlan = schedule.trainingDays.length > 0 || schedule.longRunDay
      ? applyOnboardingConfig(base, schedule.trainingDays, schedule.longRunDay)
      : base;
    setPlanEndDate(date);
    setRacePlan(racePlan);
    setSelectedWeekIndex(0);
    setq(getParamsWithSchedule(selectedUnits, selectedPlan, date, weekStartsOn, schedule));
  };

  const onSelectedUnitsChanged = (u: Units) => {
    setSelectedUnits(u);
    setq(getParamsWithSchedule(u, selectedPlan, planEndDate, weekStartsOn, { trainingDays, longRunDay }));
  };

  const toggleTrainingDay = async (day: string) => {
    const isRemoving = trainingDays.includes(day);
    const nextTrainingDays = trainingDays.includes(day)
      ? trainingDays.filter((d) => d !== day)
      : [...trainingDays, day];

    // Enforce the minimum only when *removing* days (so users can build up a valid selection).
    if (requiredDays > 0 && isRemoving && nextTrainingDays.length < requiredDays) {
      setScheduleHint(`Select at least ${requiredDays} days for the busiest week.`);
      return;
    }

    const nextLongRunDay = nextTrainingDays.includes(longRunDay)
      ? longRunDay
      : "";

    setTrainingDays(nextTrainingDays);
    setLongRunDay(nextLongRunDay);

    // Don't apply/rebuild until the selection is valid.
    if (requiredDays > 0 && nextTrainingDays.length > 0 && nextTrainingDays.length < requiredDays) {
      setScheduleHint(`Select at least ${requiredDays} days for the busiest week.`);
      return;
    }

    setScheduleHint("");

    if (racePlan) {
      const trainingPlan = await repo.fetch(selectedPlan, activeSport);
      setRequiredDays(computeRequiredDays(trainingPlan));
      const base = build(trainingPlan, planEndDate, weekStartsOn);
      const schedule = { trainingDays: nextTrainingDays, longRunDay: nextLongRunDay };
      const newRacePlan = schedule.trainingDays.length > 0 || schedule.longRunDay
        ? applyOnboardingConfig(base, schedule.trainingDays, schedule.longRunDay)
        : base;
      setRacePlan(newRacePlan);
      setq(getParamsWithSchedule(selectedUnits, selectedPlan, planEndDate, weekStartsOn, schedule));
    }
  };

  const setPreferredLongRunDay = async (day: string) => {
    // Long run day implies it's a training day.
    const nextTrainingDays = trainingDays.includes(day)
      ? trainingDays
      : [...trainingDays, day];
    const nextLongRunDay = day;

    setTrainingDays(nextTrainingDays);
    setLongRunDay(nextLongRunDay);

    // Don't apply/rebuild until the selection is valid.
    if (requiredDays > 0 && nextTrainingDays.length > 0 && nextTrainingDays.length < requiredDays) {
      setScheduleHint(`Select at least ${requiredDays} days for the busiest week.`);
      return;
    }

    setScheduleHint("");

    if (racePlan) {
      const trainingPlan = await repo.fetch(selectedPlan, activeSport);
      setRequiredDays(computeRequiredDays(trainingPlan));
      const base = build(trainingPlan, planEndDate, weekStartsOn);
      const schedule = { trainingDays: nextTrainingDays, longRunDay: nextLongRunDay };
      const newRacePlan = applyOnboardingConfig(base, schedule.trainingDays, schedule.longRunDay);
      setRacePlan(newRacePlan);
      setq(getParamsWithSchedule(selectedUnits, selectedPlan, planEndDate, weekStartsOn, schedule));
    }
  };

  function swapDates(d1: Date, d2: Date): void {
    if (racePlan) {
      const newRacePlan = swap(racePlan, d1, d2);
      setRacePlan(newRacePlan);
    }
  }

  function downloadIcalHandler() {
    if (racePlan) {
      const eventsStr = toIcal(racePlan, selectedUnits);
      if (eventsStr) {
        download(eventsStr, "plan", "ics");
      }
    }
  }

  function downloadCsvHandler() {
    if (racePlan) {
      const eventsStr = toCsv(racePlan, selectedUnits, weekStartsOn);
      if (eventsStr) {
        download(eventsStr, "plan", "csv");
      }
    }
  }

  function downloadNotionHandler() {
    if (racePlan) {
      const eventsStr = toNotion(racePlan, selectedUnits);
      if (eventsStr) {
        download(eventsStr, "plan", "md");
      }
    }
  }

  function downloadGarminHandler() {
    if (racePlan) {
      const eventsStr = toGarmin(racePlan, selectedUnits);
      if (eventsStr) {
        download(eventsStr, "plan-garmin", "json");
      }
    }
  }

  function downloadStravaHandler() {
    if (racePlan) {
      const eventsStr = toStrava(racePlan, selectedUnits);
      if (eventsStr) {
        download(eventsStr, "plan-strava", "txt");
      }
    }
  }

  async function shareHandler() {
    const shareUrl = window.location.href;
    
    // Check if Web Share API is available (mobile devices)
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'My Training Plan',
          text: 'Check out my training plan!',
          url: shareUrl,
        });
      } catch (err) {
        // User cancelled or share failed
        console.log('Share cancelled or failed:', err);
      }
    } else {
      // Fallback for desktop: copy to clipboard
      try {
        await navigator.clipboard.writeText(shareUrl);
        alert('Link copied to clipboard!');
      } catch (err) {
        console.error('Failed to copy:', err);
        // Fallback: show the URL in a prompt
        prompt('Copy this link:', shareUrl);
      }
    }
  }

  const currentWeek = racePlan?.dateGrid.weeks[selectedWeekIndex];
  const weekDistance = currentWeek ? getWeekDistance(currentWeek, selectedUnits) : [0];
  const totalWeeks = racePlan?.dateGrid.weeks.length || 0;

  // Find which week today falls into
  const today = new Date();
  const actualCurrentWeekIndex = racePlan?.dateGrid.weeks.findIndex((week) => {
    const weekStart = week.days[0]?.date;
    const weekEnd = week.days[week.days.length - 1]?.date;
    if (!weekStart || !weekEnd) return false;
    return today >= weekStart && today <= weekEnd;
  }) ?? -1;

  const totalPlanDistance = racePlan?.dateGrid.weeks.reduce((acc, week) => {
    const dist = getWeekDistance(week, selectedUnits);
    return acc + (dist.length === 2 ? dist[1] : dist[0]);
  }, 0) || 0;

  const daysUntilRace = racePlan
    ? Math.ceil((racePlan.planDates.planEndDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
    : 0;

  const weeksUntilRace = Math.floor(daysUntilRace / 7);
  const showWeeks = daysUntilRace > 7;
  const countdownValue = showWeeks ? weeksUntilRace : daysUntilRace;

  return (
    <>
      <TopNav active="dashboard" />
      <div className="dashboard-purple3">
        {/* Sidebar with gradient */}
        <aside className="sidebar-purple">
        <div className="sidebar-content">
          <a className="sidebar-logo" href="/">
            <img src={logoImage} alt="ProgressPlans" className="sidebar-logo-img" />
            <span className="logo-text">ProgressPlans</span>
          </a>
          <div className="sidebar-section">
            <h4>Settings</h4>
            
            <div className="setting-group setting-group-plan">
              <label>Training Plan</label>
              <select
                value={selectedPlan[0]}
                onChange={(e) => onSelectedPlanChange(e.target.value)}
                className="setting-select"
              >
                {availablePlansForSport.map((plan) => (
                  <option key={plan[0]} value={plan[0]}>
                    {plan[1]}
                  </option>
                ))}
              </select>
            </div>

            <div className="setting-group setting-group-race-date">
              <label>Race Date</label>
              <input
                type="date"
                value={format(planEndDate, "yyyy-MM-dd")}
                onChange={(e) => onSelectedEndDateChange(e.target.value)}
                className="setting-input"
              />
            </div>

            <div className="setting-group setting-group-units">
              <label>Units</label>
              <div className="unit-toggle">
                <button
                  className={`unit-btn ${selectedUnits === "mi" ? "active" : ""}`}
                  onClick={() => onSelectedUnitsChanged("mi")}
                >
                  Miles
                </button>
                <button
                  className={`unit-btn ${selectedUnits === "km" ? "active" : ""}`}
                  onClick={() => onSelectedUnitsChanged("km")}
                >
                  Kilometers
                </button>
              </div>
            </div>

            <div className="setting-group setting-group-workout-days">
              <label>Workout Days</label>
              {requiredDays > 0 && (
                <div style={{ color: "rgba(255, 255, 255, 0.7)", fontSize: 12, marginTop: -2 }}>
                  Selected {trainingDays.length} / Required {requiredDays}
                </div>
              )}
              {scheduleHint && (
                <div style={{ color: "rgba(255, 255, 255, 0.85)", fontSize: 12, marginTop: 6 }}>
                  {scheduleHint}
                </div>
              )}
              <div className="day-toggle" style={{ marginTop: 8 }}>
                {DAYS_OF_WEEK.map((day) => (
                  <button
                    key={day.full}
                    type="button"
                    className={`unit-btn day-btn ${trainingDays.includes(day.full) ? "active" : ""}`}
                    onClick={() => toggleTrainingDay(day.full)}
                  >
                    {day.abbr}
                  </button>
                ))}
              </div>
            </div>

            <div className="setting-group setting-group-long-run">
              <label>Long Run Day</label>
              <div className="day-toggle" style={{ marginTop: 8 }}>
                {DAYS_OF_WEEK.map((day) => {
                  const isAvailable = trainingDays.includes(day.full);
                  return (
                    <button
                      key={day.full}
                      type="button"
                      className={`unit-btn day-btn ${longRunDay === day.full ? "active" : ""}`}
                      onClick={() => setPreferredLongRunDay(day.full)}
                      disabled={!isAvailable}
                      aria-disabled={!isAvailable}
                    >
                      {day.abbr}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="sidebar-actions">
            <button className="action-btn action-btn-share" onClick={shareHandler}>
              <span>🔗</span> Share / Save
            </button>
            <button
              className="action-btn action-btn-export"
              onClick={() => setShowExportModal(true)}
            >
              <span>📥</span> Export
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content - White Area */}
      <main className="main-white">
        {/* Header */}
        <header className="header-area">
          <h1>{selectedPlan[1]}</h1>

            {/* Stats Row - Inside Header */}
            <div className="stats-row">
              <StatCard
                icon="🏁"
                label="Distance"
                value={racePlan?.raceType || "-"}
              />
              <StatCard
                icon="📅"
                label="Training Cycle"
                value={`${totalWeeks} Weeks`}
              />
              <StatCard
                icon="⏱️"
                label="Race Countdown"
                value={`${countdownValue} ${showWeeks ? "Weeks" : "Days"}`}
              />
              <StatCard
                icon="📏"
                label="This Week"
                value={`${Math.round(weekDistance.length === 2 ? weekDistance[1] : weekDistance[0])} ${selectedUnits}`}
              />
              <StatCard
                icon="📈"
                label="Total Volume"
                value={`${Math.round(totalPlanDistance)} ${selectedUnits}`}
              />
              <StatCard
                icon="🎯"
                label="Race Day"
                value={racePlan ? format(racePlan.planDates.planEndDate, "MMM d") : "-"}
              />
            </div>
          </header>

          {/* Week Selector Graph */}
          {racePlan && (
            <WeekSelector
              weeks={racePlan.dateGrid.weeks}
              selectedWeek={selectedWeekIndex}
              onSelectWeek={setSelectedWeekIndex}
              units={selectedUnits}
              currentWeekIndex={actualCurrentWeekIndex}
            />
          )}

          {/* Current Week View */}
          <div className="card week-view">
            <div className="week-view-header">
              <button
                className="week-nav-btn"
                onClick={() => setSelectedWeekIndex(Math.max(0, selectedWeekIndex - 1))}
                disabled={selectedWeekIndex === 0}
              >
                ← Previous
              </button>
              <h2>
                Week {selectedWeekIndex + 1}
              </h2>
              <button
                className="week-nav-btn"
                onClick={() => setSelectedWeekIndex(Math.min(totalWeeks - 1, selectedWeekIndex + 1))}
                disabled={selectedWeekIndex === totalWeeks - 1}
              >
                Next →
              </button>
            </div>

            <div className="days-grid">
              {currentWeek?.days.map((day, idx) => (
              <DayCard
                key={format(day.date, "yyyy-MM-dd")}
                day={day}
                units={selectedUnits}
                sourceUnits={racePlan?.sourceUnits || "mi"}
                onSwap={swapDates}
                dayIndex={idx}
              />
            ))}
            </div>
          </div>
      </main>

      {/* Export Modal */}
      {showExportModal && (
        <div className="modal-overlay" onClick={() => setShowExportModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Export Training Plan</h3>
              <button className="modal-close" onClick={() => setShowExportModal(false)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <button className="export-option-btn" onClick={() => {
                downloadIcalHandler();
                setShowExportModal(false);
              }}>
                <span className="export-icon">📥</span>
                <div className="export-text">
                  <strong>Export to iCal</strong>
                  <small>Import into Calendar apps</small>
                </div>
              </button>
              <button className="export-option-btn" onClick={() => {
                downloadCsvHandler();
                setShowExportModal(false);
              }}>
                <span className="export-icon">📄</span>
                <div className="export-text">
                  <strong>Export to CSV</strong>
                  <small>Open in Excel or Google Sheets</small>
                </div>
              </button>
              <button className="export-option-btn" onClick={() => {
                downloadNotionHandler();
                setShowExportModal(false);
              }}>
                <span className="export-icon">📝</span>
                <div className="export-text">
                  <strong>Export to Notion</strong>
                  <small>Copy Markdown into Notion</small>
                </div>
              </button>
              <button className="export-option-btn" onClick={() => {
                downloadGarminHandler();
                setShowExportModal(false);
              }}>
                <span className="export-icon">⌚</span>
                <div className="export-text">
                  <strong>Export to Garmin</strong>
                  <small>Import plan into Garmin Connect</small>
                </div>
              </button>
              <button className="export-option-btn" onClick={() => {
                downloadStravaHandler();
                setShowExportModal(false);
              }}>
                <span className="export-icon">🚴</span>
                <div className="export-text">
                  <strong>Export to Strava</strong>
                  <small>Import plan into Strava</small>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default RunDashboard;
